/**
 * NotificationBell · campanita real del Topbar conectada a core.background_jobs.
 *
 * Reemplaza la Bell estática con dot hardcoded. Comportamiento:
 *   - Suscribe a TODOS los UPDATEs de jobs del usuario actual (mismo patrón
 *     que BackgroundJobsTray, RLS hace el filtering).
 *   - Dot cyan pulsante si hay jobs running.
 *   - Dot cyan estático si hay jobs done que el user todavía no abrió la bell.
 *   - Click abre Popover (Radix) con los últimos 10 jobs.
 *   - Al abrir el popover, marca todos los done como "vistos" (localStorage).
 *   - Cada item tiene link al recurso (prospect creado, FromJobPage para
 *     analyze_prospect_deep, campaign, etc).
 */
import { useQueryClient } from '@tanstack/react-query';
import { Bell, Loader2 } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';

import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useMarkMentionRead, useMyUnreadMentions } from '@/features/chat/hooks';
import { useDueTouches } from '@/features/campaigns/social/hooks';
import { useLeadsUnread } from '@/features/leads/hooks';
import { subscribeToUserNotifications } from '@/features/notifications/api';
import {
  NOTIFICATIONS_KEY,
  useMarkAllNotificationsRead,
  useMarkNotificationRead,
  useMyNotifications,
} from '@/features/notifications/hooks';
import { createReminder } from '@/features/tasks/api/taskReminders';
import { useMyDueTasks } from '@/features/tasks/hooks';
import { TASK_KEYS } from '@/features/tasks/hooks/keys';
import { REMINDER_KEYS } from '@/features/tasks/hooks/useTaskReminders';
import { useSupabase } from '@/hooks/useSupabase';
import {
  dismissJob,
  listRecentJobs,
  subscribeToUserJobs,
  type BackgroundJob,
} from '@/services/backgroundJobsService';
import type { MyDueTasksRow } from '@/types/database.types';

import { DueTaskItem } from './notification-bell/DueTaskItem';
import { DueTouchesSection } from './notification-bell/DueTouchItem';
import { LeadsSection } from './notification-bell/LeadsSection';
import { MentionsSection } from './notification-bell/MentionsSection';
import { NotificationsSection } from './notification-bell/NotificationsSection';
import { esParaRevisar } from './notification-bell/para-revisar';
import {
  loadDismissedTaskIds,
  saveDismissedTaskIds,
} from './notification-bell/localDismiss';
import { NotificationJobItem } from './notification-bell/NotificationJobItem';
import { PanelSectionHeader } from './notification-bell/PanelSectionHeader';

// LocalStorage key para tracking de jobs vistos por usuario.
const SEEN_KEY_PREFIX = 'dendev-bell-seen-jobs-v1';

function loadSeen(userId: string): Set<string> {
  try {
    const raw = localStorage.getItem(`${SEEN_KEY_PREFIX}-${userId}`);
    if (!raw) return new Set();
    return new Set(JSON.parse(raw) as string[]);
  } catch {
    return new Set();
  }
}

function saveSeen(userId: string, seen: Set<string>) {
  try {
    // Mantener máximo 200 IDs para no crecer infinito.
    const arr = [...seen].slice(-200);
    localStorage.setItem(`${SEEN_KEY_PREFIX}-${userId}`, JSON.stringify(arr));
  } catch {
    /* noop */
  }
}

export function NotificationBell() {
  const { session } = useSupabase();
  const userId = session?.user?.id ?? null;
  const [jobs, setJobs] = useState<BackgroundJob[]>([]);
  const [seen, setSeen] = useState<Set<string>>(new Set());
  const [open, setOpen] = useState(false);

  // Tareas descartadas localmente de la campanita (por user, localStorage).
  const [dismissedTasks, setDismissedTasks] = useState<Set<string>>(new Set());
  const qc = useQueryClient();

  // Sincronización con localStorage (storage externo). No es derived state.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSeen(userId ? loadSeen(userId) : new Set());
  }, [userId]);

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setDismissedTasks(userId ? loadDismissedTaskIds(userId) : new Set());
  }, [userId]);

  // Suscripción realtime a jobs del usuario.
  useEffect(() => {
    if (!userId) return;
    let cancelled = false;

    void listRecentJobs().then((initial) => {
      if (cancelled) return;
      setJobs(initial);
    });

    const channel = subscribeToUserJobs(userId, (updated) => {
      if (cancelled) return;
      setJobs((prev) => {
        // Si fue descartado, sacarlo de la lista.
        if (updated.dismissed_at) {
          return prev.filter((j) => j.id !== updated.id);
        }
        const idx = prev.findIndex((j) => j.id === updated.id);
        if (idx === -1) return [updated, ...prev].slice(0, 10);
        const next = [...prev];
        next[idx] = updated;
        return next;
      });
    });

    return () => {
      cancelled = true;
      void channel.unsubscribe();
    };
  }, [userId]);

  // Tareas vencidas o por vencer (≤7d) del user actual.
  // El query se invalida solo cuando la Realtime de tasks dispara (ver
  // useTasksRealtimeInvalidator montado en AppLayout).
  const { data: todasLasMenciones = [] } = useMyUnreadMentions();
  const markMentionRead = useMarkMentionRead();
  /*
   * 🔴 En la campanita, una @mención de un MENSAJE ya viene por
   * core.notifications (kind `chat_mention`, o `chat_message` si fue en un DM)
   * desde el change telegram-temas-y-preferencias, y con mejor rótulo: «Hugo
   * Corzo te mencionó en #Backlogs · En el celu» en vez de «Te mencionaron ·
   * chat». Sin este filtro la misma mención se ve DOS veces, en dos secciones
   * distintas. Lo mismo con la de un COMENTARIO DE TAREA desde 2026-09-26: viene
   * como `task_mention`. Acá quedan solo las de asignaciones de tarea, que no
   * generan notificación.
   *
   * La vista Menciones de /chat y su contador SÍ leen core.mentions completa:
   * ahí el alcance es el chat y la mención es el dato, no el aviso.
   */
  const mentions = useMemo(
    () =>
      todasLasMenciones.filter(
        (m) => m.source_kind !== 'message' && m.source_kind !== 'task_comment',
      ),
    [todasLasMenciones],
  );
  // Notificaciones de core.notifications (trigger + cron): asignaciones + DMs.
  const { data: notifications = [] } = useMyNotifications();
  const markNotificationRead = useMarkNotificationRead();
  const markAllNotificationsRead = useMarkAllNotificationsRead();
  const messageNotifs = useMemo(
    () => notifications.filter((n) => n.kind === 'chat_message'),
    [notifications],
  );
  const paraRevisar = useMemo(() => notifications.filter((n) => esParaRevisar(n.kind)), [notifications]);
  const assignmentNotifs = useMemo(
    () => notifications.filter((n) => n.kind !== 'chat_message' && !esParaRevisar(n.kind)),
    [notifications],
  );

  // Realtime: nuevas notificaciones del propio user → refrescar la query.
  useEffect(() => {
    if (!userId) return;
    const channel = subscribeToUserNotifications(userId, () => {
      qc.invalidateQueries({ queryKey: NOTIFICATIONS_KEY });
    });
    return () => {
      void channel.unsubscribe();
    };
  }, [userId, qc]);
  // Respuestas de leads sin leer — realtime via useLeadsRealtimeInvalidator.
  const { data: unreadLeads = [] } = useLeadsUnread();
  const { data: rawDueTasks = [] } = useMyDueTasks();
  /**
   * Toques de campaña social vencidos.
   *
   * 🔴 Del MISMO hook que la bandeja de /campaigns y que el contador de la
   * navegación. Recalcular el predicado acá es cómo la campanita y la bandeja
   * terminan diciendo números distintos.
   *
   * Estas campañas se entregan a mano: el sistema no manda nada solo, así que
   * un toque que nadie mira es un toque que no sale. Por eso van acá y no solo
   * adentro de la pantalla de campañas.
   */
  const { data: dueTouches = [] } = useDueTouches();
  // Sacar las tareas descartadas localmente ANTES de derivar counts/dot/render.
  const dueTasks = useMemo(
    () => rawDueTasks.filter((t) => t.id == null || !dismissedTasks.has(t.id)),
    [rawDueTasks, dismissedTasks],
  );
  const overdueTasks = useMemo(
    () => dueTasks.filter((t) => t.urgency === 'overdue'),
    [dueTasks],
  );
  const dueTodayTasks = useMemo(
    () => dueTasks.filter((t) => t.urgency === 'due_today'),
    [dueTasks],
  );

  // Counts derivados.
  const runningCount = useMemo(
    () => jobs.filter((j) => j.status === 'running' || j.status === 'pending').length,
    [jobs],
  );

  const unseenDoneOrError = useMemo(
    () =>
      jobs.filter(
        (j) => (j.status === 'done' || j.status === 'error') && !seen.has(j.id),
      ),
    [jobs, seen],
  );

  const hasActivity =
    runningCount > 0
    || unseenDoneOrError.length > 0
    || overdueTasks.length > 0
    || dueTodayTasks.length > 0
    || mentions.length > 0
    || unreadLeads.length > 0
    || dueTouches.length > 0
    || notifications.length > 0;

  // Dot:
  //  - pulse si hay mentions nuevas, respuestas de leads sin leer, tareas
  //    vencidas o jobs corriendo (accionable YA)
  //  - static si solo hay jobs done sin ver o tareas due_today/soon
  const dotKind: 'pulse' | 'static' | null =
    runningCount > 0 || overdueTasks.length > 0 || mentions.length > 0
      || unreadLeads.length > 0 || notifications.length > 0
      // Un toque vencido no se manda solo: es accionable AHORA.
      || dueTouches.length > 0
      ? 'pulse'
      : unseenDoneOrError.length > 0 || dueTodayTasks.length > 0
        ? 'static'
        : null;

  // Marca los terminados como vistos al abrir.
  const handleOpenChange = useCallback(
    (next: boolean) => {
      setOpen(next);
      if (next && userId && unseenDoneOrError.length > 0) {
        setSeen((prev) => {
          const ns = new Set(prev);
          for (const j of unseenDoneOrError) ns.add(j.id);
          saveSeen(userId, ns);
          return ns;
        });
      }
    },
    [userId, unseenDoneOrError],
  );

  // Quitar una tarea de la campanita (dismiss local, no borra la tarea).
  const handleDismissTask = useCallback(
    (taskId: string) => {
      if (!userId) return;
      setDismissedTasks((prev) => {
        const ns = new Set(prev);
        ns.add(taskId);
        saveDismissedTaskIds(userId, ns);
        return ns;
      });
    },
    [userId],
  );

  // Descartar un background job (optimista + dismissed_at en DB; el realtime
  // también lo sacaría solo).
  const handleDismissJob = useCallback((jobId: string) => {
    setJobs((prev) => prev.filter((j) => j.id !== jobId));
    void dismissJob(jobId).catch(() => {
      // El UPDATE falló: el job sigue activo en DB. Re-sincronizamos para no
      // dejar la UI mintiendo (si no, reaparecería recién en el próximo fetch).
      toast.error('No se pudo descartar la notificación');
      void listRecentJobs()
        .then((fresh) => setJobs(fresh))
        .catch(() => {
          /* nada que hacer */
        });
    });
  }, []);

  // Recordar luego: persiste el recordatorio en core.task_reminders. El snooze
  // es server-side — core.v_my_due_tasks excluye la tarea mientras el
  // recordatorio esté activo (remind_at futuro + sent=false) y la vuelve a
  // mostrar sola al vencer. Sacamos la fila del cache para feedback inmediato y
  // revalidamos contra la vista (que ya la oculta). NO usamos el dismiss local:
  // es permanente y por-dispositivo, impediría que la tarea reaparezca al
  // vencer el recordatorio y no sincroniza entre dispositivos.
  const handleSnoozeTask = useCallback(
    (taskId: string, remindAtIso: string) => {
      if (!userId) return;
      void createReminder({ task_id: taskId, remind_at: remindAtIso, note: null }, userId)
        .then(() => {
          qc.setQueryData<MyDueTasksRow[]>(TASK_KEYS.myDue, (prev) =>
            prev ? prev.filter((t) => t.id !== taskId) : prev,
          );
          void qc.invalidateQueries({ queryKey: TASK_KEYS.myDue });
          void qc.invalidateQueries({ queryKey: REMINDER_KEYS.mine });
          toast.success('Te lo recuerdo más tarde');
        })
        .catch((e: Error) =>
          toast.error('No se pudo crear el recordatorio', { description: e.message }),
        );
    },
    [userId, qc],
  );

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={
            hasActivity
              ? `Notificaciones (${runningCount} corriendo, ${unseenDoneOrError.length} nuevas, ${overdueTasks.length} vencidas)`
              : 'Notificaciones'
          }
          className="text-fg-muted hover:text-fg relative h-9 w-9"
        >
          <Bell className="h-4 w-4" />
          {/* El punto no titila: que haya algo sin leer se comunica con la
              presencia del punto, no con movimiento en bucle. Las dos variantes
              quedan idénticas a propósito hasta decidir si `dotKind` sigue
              teniendo sentido. */}
          {(dotKind === 'pulse' || dotKind === 'static') && (
            <span
              aria-hidden
              className="bg-primary ring-bg-elevated absolute right-2 top-1.5 h-1.5 w-1.5 rounded-full ring-2"
            />
          )}
        </Button>
      </PopoverTrigger>
      <PopoverContent
        align="end"
        sideOffset={8}
        className="w-[380px] p-0"
      >
        <div className="border-border flex items-center justify-between border-b px-3.5 py-2.5">
          <div className="flex items-baseline gap-2">
            <h3 className="text-foreground text-body font-semibold">
              Notificaciones
            </h3>
            <span className="text-fg-dim text-micro">
              {(() => {
                const n = jobs.length + dueTasks.length + mentions.length
                  + unreadLeads.length + notifications.length + dueTouches.length;
                return n === 0 ? 'sin actividad' : `${n} item${n === 1 ? '' : 's'}`;
              })()}
            </span>
          </div>
          {runningCount > 0 && (
            <span className="text-primary inline-flex items-center gap-1.5 text-micro">
              <Loader2 className="h-3 w-3 animate-spin" />
              {runningCount} corriendo
            </span>
          )}
        </div>

        <div className="max-h-[460px] overflow-y-auto">
          {/* Bloque 000 · Lo que espera una acción tuya: un pago informado por verificar */}
          <NotificationsSection
            title="Para revisar"
            notifications={paraRevisar}
            onActivate={(id) => {
              markNotificationRead.mutate(id);
              setOpen(false);
            }}
          />

          {/* Bloque 00 · Mensajes directos (DM) */}
          <NotificationsSection
            title="Mensajes"
            notifications={messageNotifs}
            onActivate={(id) => {
              markNotificationRead.mutate(id);
              setOpen(false);
            }}
          />

          {/* Bloque 00b · Respuestas a asignaciones + pendientes estancadas */}
          <NotificationsSection
            notifications={assignmentNotifs}
            onMarkAllRead={() => markAllNotificationsRead.mutate()}
            onActivate={(id) => {
              markNotificationRead.mutate(id);
              setOpen(false);
            }}
          />

          {/* Bloque 0 · Mentions (lo más urgente: alguien te llamó por nombre) */}
          <MentionsSection
            mentions={mentions}
            onActivate={(id) => {
              markMentionRead.mutate(id);
              setOpen(false);
            }}
          />

          {/* Bloque 0.5 · Respuestas de leads sin leer — plata sobre la mesa */}
          <LeadsSection leads={unreadLeads} onActivate={() => setOpen(false)} />

          {/* Bloque 0.7 · Toques de campaña que ya tocaban.
              Van antes de las tareas porque el toque no se manda solo: si nadie
              lo copia y lo pega hoy, la secuencia se corta. */}
          <DueTouchesSection touches={dueTouches} onNavigate={() => setOpen(false)} />

          {/* Bloque 1 · Tareas vencidas / por vencer.
              Las mostramos arriba porque son accionables ahora — un job en
              progreso puede esperar; una tarea overdue no. */}
          {dueTasks.length > 0 && (
            <div>
              <PanelSectionHeader
                title="Tareas"
                aside={
                  <span>
                    {overdueTasks.length > 0 && (
                      <span className="text-err">{overdueTasks.length} vencidas</span>
                    )}
                    {overdueTasks.length > 0 && dueTodayTasks.length > 0 && ' · '}
                    {dueTodayTasks.length > 0 && (
                      <span className="text-warn">{dueTodayTasks.length} hoy</span>
                    )}
                  </span>
                }
              />
              <ul>
                {dueTasks.slice(0, 8).map((t) => (
                  <DueTaskItem
                    key={t.id}
                    task={t}
                    onNavigate={() => setOpen(false)}
                    onDismiss={() => t.id && handleDismissTask(t.id)}
                    onSnooze={(iso) => t.id && handleSnoozeTask(t.id, iso)}
                  />
                ))}
              </ul>
            </div>
          )}

          {/* Bloque 2 · Background jobs. */}
          {jobs.length > 0 && (
            <div>
              {dueTasks.length > 0 && (
                <PanelSectionHeader title="Procesos" />
              )}
              <ul>
                {jobs.map((job) => (
                  <NotificationJobItem
                    key={job.id}
                    job={job}
                    onNavigate={() => setOpen(false)}
                    onDismiss={() => handleDismissJob(job.id)}
                  />
                ))}
              </ul>
            </div>
          )}

          {jobs.length === 0 && dueTasks.length === 0
            && mentions.length === 0 && unreadLeads.length === 0
            && notifications.length === 0 && (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-10 text-center">
              <Bell className="text-fg-dim h-6 w-6" />
              <p className="text-muted-foreground text-caption">
                Todo al día. Sin respuestas de leads, tareas vencidas ni procesos en curso.
              </p>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
